/**
 * Who a site's alerts reach beyond typed recipient lists, and the one alert only the cloud can
 * raise. Cloud page, one site at a time.
 *
 *   OfflineAlertCard   "site offline" — the cloud notices a site stopped reporting and tells that
 *                      site's people (its system recipients, plus its users when SiteUsersCard
 *                      says so). Always sent by the cloud: a site cannot report its own outage.
 *   SiteUsersCard      also email the users granted this site in Site access — system and offline
 *                      alerts. Admins, who see every site, are not added: a site's alerts reach
 *                      that site's people only.
 */
import { useEffect, useState } from 'react';
import { SignalSlashIcon, UsersIcon } from '@heroicons/react/24/outline';
import { Callout } from '../common/Callout';
import { AACard, AASwitch, aaInputCls } from './AlertActionsControls';
import { alertActionsErrorText, type OfflineAlertValue, type SiteUser } from './alertActionsModel';

const AFTER_CHOICES = [60, 120, 300, 600, 900, 1800, 3600];
const afterLabel = (s: number) => (s < 120 ? `${s / 60} minute` : s < 3600 ? `${s / 60} minutes` : '1 hour');

function useSaver() {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setSaving(true);
    setResult(null);
    try {
      await fn();
      setResult({ tone: 'success', text: 'Saved' });
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Could not save') });
    } finally {
      setSaving(false);
    }
  };
  return { saving, result, run };
}

export function OfflineAlertCard({
  value,
  onSave,
  canEdit,
  recipientsSummary,
}: {
  value: OfflineAlertValue | null;
  onSave: (next: OfflineAlertValue) => Promise<unknown>;
  canEdit: boolean;
  /** Who it goes to, in words (e.g. "2 emails, 1 number + 3 site users"). */
  recipientsSummary?: string;
}) {
  const [form, setForm] = useState<OfflineAlertValue>({ enabled: true, after_seconds: 120 });
  const { saving, result, run } = useSaver();
  useEffect(() => { if (value) setForm(value); }, [value]);
  const dirty = !!value && (form.enabled !== value.enabled || form.after_seconds !== value.after_seconds);
  const choices = AFTER_CHOICES.includes(form.after_seconds) ? AFTER_CHOICES : [...AFTER_CHOICES, form.after_seconds].sort((a, b) => a - b);

  return (
    <AACard
      icon={SignalSlashIcon}
      title="Site offline"
      subtitle="The cloud tells this site's people when the site stops reporting, and again when it is back. Always sent by the cloud — a site cannot report its own outage."
      right={<AASwitch checked={form.enabled} disabled={!canEdit || !value} label="Send site offline alerts" onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />}
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <span className={`min-w-0 flex-1 text-[12px] ${result?.tone === 'danger' ? 'text-destructive' : 'text-muted-foreground'}`}>
            {result?.text || (dirty ? 'Unsaved changes' : '')}
          </span>
          <button
            type="button"
            onClick={() => run(() => onSave(form))}
            disabled={!canEdit || saving || !dirty}
            className="rounded-lg bg-brand px-3 py-1.5 text-[12px] font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className={`flex flex-wrap items-center gap-2 text-[13px] text-foreground ${form.enabled ? '' : 'opacity-50'}`}>
        <span>Alert when the site has not reported for</span>
        <select
          value={form.after_seconds}
          disabled={!canEdit || !form.enabled}
          onChange={(e) => setForm((f) => ({ ...f, after_seconds: Number(e.target.value) }))}
          className={`${aaInputCls} mt-0 w-auto`}
        >
          {choices.map((s) => <option key={s} value={s}>{afterLabel(s)}</option>)}
        </select>
      </div>
      {recipientsSummary && <p className="mt-2 text-[11px] text-muted-foreground">Goes to: {recipientsSummary}</p>}
    </AACard>
  );
}

export function SiteUsersCard({
  value,
  users,
  onSave,
  canEdit,
}: {
  value: boolean | null;
  /** The users it would reach; null while loading. */
  users: SiteUser[] | null;
  onSave: (next: boolean) => Promise<unknown>;
  canEdit: boolean;
}) {
  const { saving, result, run } = useSaver();
  return (
    <AACard
      icon={UsersIcon}
      title="Also email the users who have access to this site"
      subtitle="Its system alerts also go to every active user granted this site in Site access. Admins see every site and are not added."
      right={
        <AASwitch
          checked={!!value}
          disabled={!canEdit || value === null}
          busy={saving}
          label="Also email the site's users"
          onChange={(v) => run(() => onSave(v))}
        />
      }
    >
      {users === null ? (
        <div className="h-8 animate-pulse rounded-lg bg-muted" />
      ) : users.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">Nobody has been granted this site yet (User management → Site access).</p>
      ) : (
        <div className={`flex flex-wrap gap-1.5 ${value ? '' : 'opacity-60'}`}>
          {users.map((u) => (
            <span key={u.id} title={u.email} className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[12px] text-foreground">
              {u.name || u.email}
            </span>
          ))}
        </div>
      )}
      {result?.tone === 'danger' && <Callout tone="danger" className="mt-2">{result.text}</Callout>}
    </AACard>
  );
}
